import { Portal } from '@mui/material';

import {
  DrawerProvider,
  DynamicGrid,
  DynamicGridActionBar,
  DynamicGridBackdrop,
  DynamicGridProvider,
  MarketingBannersStack,
} from '@cast/design-system';

import {
  WORKLOADS_DASHBOARD_DEFAULT_VISIBLE,
  WORKLOADS_DASHBOARD_ITEM_CATEGORY,
  WORKLOADS_DASHBOARD_ITEMS_BY_CATEGORY,
  WorkloadsDashboardItemId,
  getWorkloadsDashboardStorageKey,
} from './_constants';
import {
  WORKLOAD_CATEGORIES,
  WORKLOAD_CATEGORY_FILTER_OPTIONS,
} from '../../_constants';
import type { WorkloadCategory } from '../../_constants';
import {
  INVENTORY_WORKLOADS_SETTINGS_SLOT_ID,
  InventoryWorkloadsFilters,
} from '../../_components/InventoryWorkloadsFilters';
import { InventoryWorkloadsCategoryToggle } from '../../_components/InventoryWorkloadsCategoryToggle';
import { InventoryWorkloadsRangePicker } from '../../_components/InventoryWorkloadsRangePicker';
import { createInventoryCustomizeDrawer } from '../../_components/InventoryDashboardCustomizeDrawer/InventoryDashboardCustomizeDrawer';
import { CpuUtilizationChart } from './autoscaling/CpuUtilizationChart';
import { MemoryUtilizationChart } from './autoscaling/MemoryUtilizationChart';
import { WorkloadAutoscalerCoverageCard } from './autoscaling/WorkloadAutoscalerCoverageCard';
import { RecommendationStatusCard } from './autoscaling/RecommendationStatusCard';
import { ScalingPoliciesCard } from './autoscaling/ScalingPoliciesCard';
import { CpuImpactChart } from './savings/CpuImpactChart';
import { MemoryImpactChart } from './savings/MemoryImpactChart';
import { AutoscalerImpactCard } from './savings/AutoscalerImpactCard';
import { ReclaimedResourcesCard } from './savings/ReclaimedResourcesCard';
import { SavingsSummaryCard } from './savings/SavingsSummaryCard';
import { NetworkCostCard } from './network/NetworkCostCard';
import { NetworkCostOverTimeCard } from './network/NetworkCostOverTimeCard';
import { NetworkTrafficCard } from './network/NetworkTrafficCard';
import { GpuWorkloadsCard } from './gpu/GpuWorkloadsCard';
import { GpuMemoryWasteCard } from './gpu/GpuMemoryWasteCard';
import { GpuIdleSpendCard } from './gpu/GpuIdleSpendCard';
import { P95LatencyDistributionCard } from './reliability/P95LatencyDistributionCard';
import { ReliabilityMapCard } from './reliability/ReliabilityMapCard';
import { WorkloadHealthCard } from './reliability/WorkloadHealthCard';
import { WorkloadHealthTrafficShareCard } from './reliability/WorkloadHealthTrafficShareCard';
import { BiggestCostMoversCard } from './compute/BiggestCostMoversCard';
import { ComputeCostCard } from './compute/ComputeCostCard';
import { ComputeSpendChart } from './compute/ComputeSpendChart';
import { InventoryWorkloadsDashboardCustomizeButton } from '../../_components/InventoryWorkloadsDashboardCustomizeButton';
import { InventoryWorkloadsAutoscalerBanner } from './InventoryWorkloadsAutoscalerBanner';

const WorkloadsCustomizeDrawer = createInventoryCustomizeDrawer(
  WORKLOADS_DASHBOARD_ITEM_CATEGORY,
  WORKLOAD_CATEGORY_FILTER_OPTIONS
);

type InventoryWorkloadsDashboardArgs = {
  category?: WorkloadCategory;
};

export const InventoryWorkloadsDashboard = ({
  category = WORKLOAD_CATEGORIES.DEFAULT,
}: InventoryWorkloadsDashboardArgs) => {
  const storageKey = getWorkloadsDashboardStorageKey(category);
  const defaultVisibleItems =
    WORKLOADS_DASHBOARD_DEFAULT_VISIBLE[category] ??
    WORKLOADS_DASHBOARD_DEFAULT_VISIBLE[WORKLOAD_CATEGORIES.DEFAULT];
  const items =
    WORKLOADS_DASHBOARD_ITEMS_BY_CATEGORY[category] ??
    WORKLOADS_DASHBOARD_ITEMS_BY_CATEGORY[WORKLOAD_CATEGORIES.DEFAULT];

  return (
    <>
      <InventoryWorkloadsFilters
        categoryToggle={<InventoryWorkloadsCategoryToggle />}
        dateControl={<InventoryWorkloadsRangePicker />}
      />
      <MarketingBannersStack>
        <InventoryWorkloadsAutoscalerBanner key="inventory-autoscaler-banner" />
      </MarketingBannersStack>
      <DynamicGridProvider
        key={storageKey}
        storageKey={storageKey}
        defaultVisibleItems={defaultVisibleItems}
        items={items}
      >
        <DynamicGridBackdrop />
        <DrawerProvider>
          <Portal
            container={() =>
              document.getElementById(INVENTORY_WORKLOADS_SETTINGS_SLOT_ID)
            }
          >
            <InventoryWorkloadsDashboardCustomizeButton />
          </Portal>
          <DynamicGrid compactor="none">
            <CpuUtilizationChart
              key={WorkloadsDashboardItemId.CpuUtilization}
            />
            <MemoryUtilizationChart
              key={WorkloadsDashboardItemId.MemoryUtilization}
            />
            <WorkloadAutoscalerCoverageCard
              key={WorkloadsDashboardItemId.WorkloadAutoscalerCoverage}
            />
            <RecommendationStatusCard
              key={WorkloadsDashboardItemId.RecommendationStatus}
            />
            <ScalingPoliciesCard
              key={WorkloadsDashboardItemId.ScalingPolicies}
            />
            <CpuImpactChart key={WorkloadsDashboardItemId.CpuImpact} />
            <MemoryImpactChart key={WorkloadsDashboardItemId.MemoryImpact} />
            <AutoscalerImpactCard
              key={WorkloadsDashboardItemId.AutoscalerImpact}
            />
            <ReclaimedResourcesCard
              key={WorkloadsDashboardItemId.ReclaimedResources}
            />
            <SavingsSummaryCard key={WorkloadsDashboardItemId.SavingsSummary} />
            <NetworkCostCard key={WorkloadsDashboardItemId.NetworkCost} />
            <NetworkTrafficCard key={WorkloadsDashboardItemId.NetworkTraffic} />
            <NetworkCostOverTimeCard
              key={WorkloadsDashboardItemId.NetworkCostOverTime}
            />
            <GpuWorkloadsCard key={WorkloadsDashboardItemId.GpuWorkloads} />
            <GpuMemoryWasteCard key={WorkloadsDashboardItemId.GpuMemoryWaste} />
            <GpuIdleSpendCard key={WorkloadsDashboardItemId.GpuIdleSpend} />
            <ReliabilityMapCard key={WorkloadsDashboardItemId.ReliabilityMap} />
            <WorkloadHealthCard key={WorkloadsDashboardItemId.WorkloadHealth} />
            <WorkloadHealthTrafficShareCard
              key={WorkloadsDashboardItemId.WorkloadHealthTrafficShare}
            />
            <P95LatencyDistributionCard
              key={WorkloadsDashboardItemId.P95LatencyDistribution}
            />
            <ComputeCostCard key={WorkloadsDashboardItemId.ComputeCost} />
            <BiggestCostMoversCard
              key={WorkloadsDashboardItemId.BiggestCostMovers}
            />
            <ComputeSpendChart key={WorkloadsDashboardItemId.ComputeSpend} />
          </DynamicGrid>
          <DynamicGridActionBar
            drawerTitle="Customize dashboard"
            drawerComponent={WorkloadsCustomizeDrawer}
          />
        </DrawerProvider>
      </DynamicGridProvider>
    </>
  );
};
